import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Users, X } from 'lucide-react';
import type { ContactSuggestion } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { relativeTime } from '../../lib/format';
import { Avatar, Button, Card, CardBody, CardHeader, ErrorState, IconButton, SkeletonLines, useToast } from '../ui';

const DISMISS_KEY = 'mp:dismissed-contact-suggestions';

function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const v: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
function writeDismissed(list: string[]) {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable — dismissal lasts for this session only */
  }
}

/** "Frequent correspondents" — reviewable suggestions, never auto-saved. */
export function ContactSuggestions() {
  const qc = useQueryClient();
  const toast = useToast();
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);
  const [showAll, setShowAll] = useState(false);
  const q = useQuery({ queryKey: qk.contactSuggestions, queryFn: () => api('GET /api/contacts/suggestions') });

  const save = useMutation({
    mutationFn: (s: ContactSuggestion) => api('POST /api/contacts', { body: { name: s.name?.trim() ?? '', emails: [s.email], source: 'suggestion' } }),
    onSuccess: (r) => {
      toast.success(`${r.contact.name || r.contact.emails[0] || 'Contact'} saved to contacts`);
      void qc.invalidateQueries({ queryKey: qk.contactSuggestions });
      void qc.invalidateQueries({ queryKey: qk.contactsRoot });
    },
    onError: (e) => toast.error(e),
  });

  // Hide immediately; the server records the dismissal so it syncs to every device.
  const dismiss = (email: string) => {
    const next = Array.from(new Set([...dismissed, email.toLowerCase()]));
    setDismissed(next);
    writeDismissed(next);
    api('POST /api/contacts/suggestions/dismiss', { body: { email } })
      .then(() => qc.invalidateQueries({ queryKey: qk.contactSuggestions }))
      .catch((e) => toast.error(e));
  };

  const visible = (q.data?.suggestions ?? []).filter((s) => !dismissed.includes(s.email.toLowerCase()));
  if (q.isSuccess && visible.length === 0) return null;
  const shown = showAll ? visible : visible.slice(0, 5);

  return (
    <Card>
      <CardHeader
        icon={<Users className="h-5 w-5" />}
        title="Frequent correspondents"
        description="People you email often who aren't saved yet. Review each one — nothing is saved automatically."
      />
      <CardBody className="pt-4">
        {q.isLoading ? (
          <SkeletonLines lines={3} />
        ) : q.isError ? (
          <ErrorState compact error={q.error} onRetry={() => void q.refetch()} />
        ) : (
          <>
            <ul className="-mx-3 grid gap-0.5 md:grid-cols-2">
              {shown.map((s) => (
                <li key={s.email} className="group flex items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-raised">
                  <Avatar name={s.name} email={s.email} size={36} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[0.8125rem] font-medium text-ink">{s.name ?? s.email}</p>
                    <p className="truncate text-xs text-muted">
                      {s.name ? `${s.email} · ` : ''}
                      {s.messageCount} {s.messageCount === 1 ? 'message' : 'messages'} · last {relativeTime(s.lastInteractionAt)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                    <Button size="sm" variant="soft" icon={<UserPlus className="h-4 w-4" />} loading={save.isPending && save.variables?.email === s.email} onClick={() => save.mutate(s)}>
                      Save
                    </Button>
                    <IconButton size="sm" label={`Dismiss ${s.email}`} icon={<X className="h-4 w-4" />} onClick={() => dismiss(s.email)} />
                  </div>
                </li>
              ))}
            </ul>
            {visible.length > 5 && (
              <Button size="sm" variant="ghost" className="mt-2" onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Show fewer' : `Show all ${visible.length}`}
              </Button>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}
